// Gallery custom del PDP.
//
// Sincroniza main slide (scroll-snap horizontal) con la tira de thumbnails
// que sigue a la miniatura activa al cambiar de foto. Fórmula del diseñador
// para el centrado del track:
//
//   scrollLeft = max(0, n * 80 - (containerWidth - 72) / 2)
//
// donde 80 es el stride por slot (72 thumb + 8 gap) y 72 es el ancho de la
// thumb. Restar (containerWidth - 72) / 2 centra la thumb en el viewport
// visible del track.
//
// Sync bidireccional:
//   · click en thumb → scrollea main a la slide N + marca thumb activa +
//     centra el track sobre la thumb activa.
//   · scroll en main (por swipe o arrows) → detecta slide activa por
//     IntersectionObserver → marca thumb activa + centra track.
//   · arrows click → navega ±1 slide en main → dispara el flujo de arriba.
//
// Sin dependencias externas. Loop init busca todas las instancias en DOM
// ready y también tras cualquier morph de section (Section Rendering API).

const THUMB_STRIDE = 80; // 72 thumb + 8 gap
const THUMB_WIDTH = 72;

function initGallery(root) {
  if (root.dataset.the90sGalleryReady === '1') return;
  root.dataset.the90sGalleryReady = '1';

  const slidesTrack = root.querySelector('[data-gallery-slides]');
  const thumbsTrack = root.querySelector('[data-gallery-thumbs]');
  const counterEl = root.querySelector('[data-gallery-counter]');
  const prevBtn = root.querySelector('[data-gallery-prev]');
  const nextBtn = root.querySelector('[data-gallery-next]');

  if (!slidesTrack) return;

  const slides = Array.from(slidesTrack.children);
  const thumbs = thumbsTrack ? Array.from(thumbsTrack.querySelectorAll('[data-thumb-index]')) : [];
  const count = slides.length;
  if (count <= 1) return; // Sin thumbs, contador ni arrows relevantes con una sola slide.

  // ---- Estado + helpers ---------------------------------------------------

  let currentIndex = 0;

  function updateThumbs(index) {
    for (const thumb of thumbs) {
      const isActive = Number(thumb.dataset.thumbIndex) === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  function centerThumbStrip(index) {
    if (!thumbsTrack) return;
    // Fórmula del diseñador. containerWidth = ancho del viewport visible del track.
    const target = Math.max(0, index * THUMB_STRIDE - (thumbsTrack.clientWidth - THUMB_WIDTH) / 2);
    thumbsTrack.scrollTo({ left: target, behavior: 'smooth' });
  }

  function updateArrows(index) {
    if (prevBtn) prevBtn.setAttribute('aria-disabled', index === 0 ? 'true' : 'false');
    if (nextBtn) nextBtn.setAttribute('aria-disabled', index >= count - 1 ? 'true' : 'false');
  }

  function scrollMainToSlide(index) {
    const slide = slides[index];
    if (!slide) return;
    // scrollIntoView con block:nearest evita scroll vertical de la página.
    slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  function updateCounter(index) {
    if (!counterEl) return;
    counterEl.textContent = `${index + 1}/${count}`;
  }

  function setActive(index, opts) {
    const clampedIndex = Math.max(0, Math.min(count - 1, index));
    if (clampedIndex === currentIndex && !opts?.force) return;
    currentIndex = clampedIndex;
    updateThumbs(clampedIndex);
    updateArrows(clampedIndex);
    updateCounter(clampedIndex);
    centerThumbStrip(clampedIndex);
  }

  // ---- Sync main → thumbs vía IntersectionObserver ------------------------

  const io = new IntersectionObserver((entries) => {
    // La slide con mayor intersectionRatio en el viewport del track es la activa.
    let best = null;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
    }
    if (!best) return;
    const index = Number(best.target.dataset.slideIndex);
    if (Number.isFinite(index)) setActive(index);
  }, {
    root: slidesTrack,
    threshold: [0.5, 0.75, 1],
  });
  for (const slide of slides) io.observe(slide);

  // ---- Click en thumb -----------------------------------------------------

  if (thumbsTrack) {
    thumbsTrack.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const btn = event.target.closest('[data-thumb-index]');
      if (!btn) return;
      const index = Number(btn.dataset.thumbIndex);
      if (Number.isFinite(index)) {
        scrollMainToSlide(index);
        // El IntersectionObserver capturará el cambio y actualizará thumbs + arrows.
      }
    });
  }

  // ---- Arrows -------------------------------------------------------------

  if (prevBtn) {
    prevBtn.addEventListener('click', (event) => {
      if (prevBtn.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
      scrollMainToSlide(currentIndex - 1);
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', (event) => {
      if (nextBtn.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
      scrollMainToSlide(currentIndex + 1);
    });
  }

  // ---- Init state ---------------------------------------------------------

  setActive(0, { force: true });
}

function initAll(root = document) {
  for (const el of root.querySelectorAll('[data-the90s-gallery]')) initGallery(el);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

// Re-init cuando Shopify re-morfa una section (editor del tema, Section Rendering API).
document.addEventListener('shopify:section:load', (e) => initAll(e.target || document));
